import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useFeatureFlag } from '@fs/zion-flags'
import { setPreference, usePreference } from '@fs/zion-preferences'

const DARK_MODE_FEATURE_FLAG = 'rip_sharedImageViewer_darkMode'
const DARK_MODE_PREFERENCE_KEY = 'shared-image-viewer.darkMode'

const DEFAULT_DARK_MODE_CONTEXT = {
  isDarkModeOn: false,
  loading: false,
  setIsDarkModeOn: () => {},
  resetDarkMode: () => {},
  theme: 'day',
  setIsDarkModeAllowedOnPage: () => {},
}

const DarkModeContext = createContext(undefined)

export default function DarkModeProvider({ children }) {
  const { isOn: isDarkModeFeatureEnabled } = useFeatureFlag(DARK_MODE_FEATURE_FLAG)
  const [isDarkModeOn, setIsDarkModeOn] = useState()
  const [isDarkModeAllowedOnPage, setIsDarkModeAllowedOnPage] = useState(false)
  const initialDarkModePreference = usePreference(DARK_MODE_PREFERENCE_KEY)

  useEffect(() => {
    if (typeof initialDarkModePreference === 'boolean') {
      setIsDarkModeOn(initialDarkModePreference)
    } else {
      setIsDarkModeOn(false)
    }
  }, [initialDarkModePreference])

  const setDarkMode = useCallback(
    async (isOn) => {
      setIsDarkModeOn(isOn)
      if (!isDarkModeFeatureEnabled) return

      try {
        await setPreference(DARK_MODE_PREFERENCE_KEY, isOn)
      } catch (error) {
        console.error('Failed to persist dark mode preference', error)
      }
    },
    [isDarkModeFeatureEnabled]
  )

  const resetDarkMode = useCallback(async () => {
    if (!isDarkModeFeatureEnabled) return

    setIsDarkModeOn(false)

    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(DARK_MODE_PREFERENCE_KEY)
    }

    try {
      await setPreference(DARK_MODE_PREFERENCE_KEY, false)
    } catch (error) {
      console.error('Failed to reset dark mode preference', error)
    }
  }, [isDarkModeFeatureEnabled])

  const theme = isDarkModeFeatureEnabled && isDarkModeOn && isDarkModeAllowedOnPage ? 'night' : 'day'

  const value = useMemo(
    () => ({
      isDarkModeOn: isDarkModeFeatureEnabled ? isDarkModeOn : false,
      loading: isDarkModeFeatureEnabled ? isDarkModeOn === undefined : false,
      setIsDarkModeOn: isDarkModeFeatureEnabled ? setDarkMode : DEFAULT_DARK_MODE_CONTEXT.setIsDarkModeOn,
      resetDarkMode: isDarkModeFeatureEnabled ? resetDarkMode : DEFAULT_DARK_MODE_CONTEXT.resetDarkMode,
      theme: isDarkModeFeatureEnabled ? theme : DEFAULT_DARK_MODE_CONTEXT.theme,
      setIsDarkModeAllowedOnPage,
    }),
    [isDarkModeFeatureEnabled, isDarkModeOn, resetDarkMode, setDarkMode, theme]
  )

  return <DarkModeContext.Provider value={value}>{children}</DarkModeContext.Provider>
}

export function useDarkModeContext() {
  const context = useContext(DarkModeContext)
  if (!context) {
    throw new Error(
      'useDarkModeContext must be used within a DarkModeProvider. If writing a test, use <StateProvider> from testUtils.js'
    )
  }
  return context
}

export { DarkModeContext }
